const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/labReports/loadCumulDataHandlers.ts';
import Imm from 'immutable';
import { decCounter, incCounter } from '../../utils/reducerUtils';
import { IKisApiHandlersMap } from '../../defs/actionTypes';
import { LoadCumulDataAction } from '../../actions/labReportsActions';
import { Logger } from '../../services/KisLoggerService';
import {
    estimateImportance,
    findIdxOfAvailItem,
    generateKey,
    getCanonicalUnits,
    processCellData,
    processRefRange,
} from '../../utils/labReportUtils';

const Log = Logger('labReports/loadCumulData');
const emptyList = Imm.List<any>();
const emptyMap = Imm.Map<string, any>();

export const loadCumulDataHandlers: IKisApiHandlersMap<LoadCumulDataAction> = {
    pending: (state, action) => {
        const repId = `${action.meta.patnr}_${action.meta.defsId}`;
        return incCounter(state, ['cumulReports', repId, 'loading']);
    },
    success: (state, action) => {
        const repId = `${action.meta.patnr}_${action.meta.defsId}`;

        try {
            const payload = action && action.payload ? Imm.fromJS(action.payload) : emptyMap;

            // Find the report in the availList.

            const availIdx = findIdxOfAvailItem(
                state.getIn(['cumulReports', repId]),
                action.meta.sender,
                action.meta.reportnr
            );

            if (availIdx < 0) {
                throw new Error('The item in availList could not be found!');
            }

            // Determine the colId.

            let availItem = state.getIn(['cumulReports', repId, 'availList', availIdx]);
            let colId: string = availItem.get('colId');

            if (!colId) {
                // The column wasn't found. Create a unique id for it!
                const n = state.getIn(['cumulReports', repId, 'nextColId']) || 1;
                state = state.setIn(['cumulReports', repId, 'nextColId'], n + 1);
                colId = `${n}`;
            }

            // Rewrite data cells.

            let newData = emptyMap;

            (payload.getIn(['Result', 'Data']) || emptyMap).forEach((entry: any, rowId: string) => {
                newData = newData.set(`${colId}_${rowId}`, entry);
            });

            // Put the colId into the avail item and mark it as loaded.

            availItem = availItem.set('colId', colId).delete('stale');
            state = state.setIn(['cumulReports', repId, 'availList', availIdx], availItem);

            // Iterate over the payload's rowDefs and merge them with the state.

            let rowDefs = state.getIn(['cumulReports', repId, 'rowDefs']) || Imm.OrderedMap<string, any>();
            const newRowDefs = payload.getIn(['Result', 'RowDefs']) || emptyList;
            let mergedRowDefs = rowDefs;
            let rewriteMap: any = {}; // key: fromId, val: { toId, cell }
            const affectedRows: any = {}; // key: rowId, val: true

            newRowDefs.forEach((d: Imm.Map<string, any>) => {
                // Extract the rawRowId.

                const rawRowId = d.get('RowId');
                d = d.delete('RowId');

                // Delete reserved keys.

                d = d.delete('minVal').delete('maxVal').delete('refList').delete('refMap');

                // Process the Units member.

                if (d.has('Units')) {
                    // Unfortunately, the spelling of Units is not entirely consistent. We transform it to the canonical
                    // spelling, which help us when matching rowDefs from multiple calls. Moreover, certain Units are
                    // zapped, because they aren't units as such.

                    const canon = getCanonicalUnits(d.get('Units'));

                    if (!canon) {
                        d = d.delete('Units');
                    } else {
                        d = d.set('Units', canon);
                    }
                }

                // Find the row in the existing rowDefs.

                const foundRowId = rowDefs.reduce((result: string, oldDef: Imm.Map<string, any>, oldRowId: string) => {
                    // When a row is matched, the existing entry will be updated, otherwise a new row will be added to
                    // rowDefs.

                    if (result) {
                        return result;
                    } else {
                        const sameLabId = (d.get('LabId') || '') === (oldDef.get('LabId') || '');
                        const samePos = (d.get('Pos') || '') === (oldDef.get('Pos') || '');
                        const sameName = (d.get('Name') || '') === (oldDef.get('Name') || '');
                        const sameUnits = (d.get('Units') || '') === (oldDef.get('Units') || '');
                        return sameLabId && samePos && sameName && sameUnits ? oldRowId : '';
                    }
                }, '');

                let uniqueRowId: string;

                if (foundRowId) {
                    // The row already exists.
                    uniqueRowId = foundRowId;
                } else {
                    // The row wasn't found. Create a unique id for it!
                    const n = state.getIn(['cumulReports', repId, 'nextRowId']) || 1;
                    state = state.setIn(['cumulReports', repId, 'nextRowId'], n + 1);
                    uniqueRowId = `${n}`;
                    mergedRowDefs = mergedRowDefs.set(uniqueRowId, d);
                }

                // If the row id differs, unlink the cell, because its cellId needs to be rewritten.

                if (rawRowId !== uniqueRowId) {
                    const fromId = `${colId}_${rawRowId}`;
                    const cell = newData.get(fromId);

                    if (cell) {
                        newData = newData.delete(fromId);
                        rewriteMap[fromId] = {
                            toId: `${colId}_${uniqueRowId}`,
                            cell,
                        };
                    }
                }

                // Process Ref ranges.

                const rawRef = d.get('Ref');
                mergedRowDefs = mergedRowDefs.deleteIn([uniqueRowId, 'Ref']);

                if (typeof rawRef === 'string' && rawRef.trim() && rawRef.trim() !== '-') {
                    mergedRowDefs = processRefRange(rawRef, mergedRowDefs, uniqueRowId, availItem);
                }

                // Remember the uniqueRowId for later.
                affectedRows[uniqueRowId] = true;
            });

            // Reinsert unlinked cells with their cellIds rewritten.

            for (const fromId in rewriteMap) {
                if (rewriteMap.hasOwnProperty(fromId)) {
                    const toId = rewriteMap[fromId].toId;

                    if (newData.has(toId)) {
                        // Oops, this shouldn't happen!
                        Log.error(`Cell id already occupied: ${toId}`);
                    } else {
                        newData = newData.set(toId, rewriteMap[fromId].cell);
                    }
                }
            }

            // Sort mergedRowDefs by Pos.

            mergedRowDefs = mergedRowDefs.sort((row1: Imm.Map<string, any>, row2: Imm.Map<string, any>) => {
                const p1 = row1.get('Pos') || 'ZZZZ';
                const p2 = row2.get('Pos') || 'ZZZZ';
                return p1 < p2 ? -1 : p1 > p2 ? 1 : 0;
            });

            // Put the mergedRowDefs back into the state.

            state = state.setIn(['cumulReports', repId, 'rowDefs'], mergedRowDefs);
            rowDefs = null; // no longer needed
            mergedRowDefs = null; // ditto

            // Merge comments from the payload with the ones in the state.

            let comments = state.getIn(['cumulReports', repId, 'comments']) || emptyMap;
            const newComments = payload.getIn(['Result', 'Comments']) || emptyMap;
            rewriteMap = {}; // key: fromId, value: { toId, cellIds }

            newComments.forEach((commentText: string, rawId: string) => {
                // The rawIds are arbitrarily assigned by KISAPI and may randomly collide with existing ids. The following
                // algorithm reassigns ids in order to keep the legend small and the ids stable.

                let foundId = '';

                comments.forEach((c: Imm.Map<string, any>, id: string) => {
                    if (commentText === c.get('text')) {
                        foundId = id;
                        return false; // break forEach
                    }
                });

                let addWithId = '';

                if (foundId === rawId) {
                    // Found the comment with the same text and id!
                    // Nothing is to be added or rewritten.
                    // Log.info (`Found the comment with the same text and id: ${rawId}`);
                } else if (foundId) {
                    // Found a comment with the same text, but the id differs!
                    // rawId is to be dropped in favour of foundId.
                    // Log.info (`Found a comment with the same text, but the id differs: ${rawId} vs. ${foundId}`);
                    rewriteMap[rawId] = { toId: foundId };
                } else if (comments.has(rawId)) {
                    // Found a comment with the same id, but the text differs!
                    // Add a new comment under a new, unique id!
                    // Log.info (`Found a comment with the same id (${rawId}), but the text differs`);
                    addWithId = generateKey({
                        forMap: comments,
                        sourceText: commentText,
                        chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789*+#§$£',
                        allowCharsNotInSource: true,
                        allowNumerical: true,
                    });
                    rewriteMap[rawId] = { toId: addWithId };
                } else {
                    // Both the text and the id seem to be unused.
                    // Add a new comment, using the raw id as the commentId.
                    // Log.info (`Both the text and the id (${rawId}) seem to be unused.`);
                    addWithId = rawId;
                }

                if (addWithId) {
                    // Add the comment.
                    comments = comments.set(
                        addWithId,
                        Imm.fromJS({
                            text: commentText,
                            importance: estimateImportance(commentText),
                        })
                    );
                }
            });

            // Rewrite cell data to refer to the actual commentIds.

            for (const fromId in rewriteMap) {
                if (rewriteMap.hasOwnProperty(fromId)) {
                    rewriteMap[fromId].cellIds = newData
                        .filter((cell: Imm.Map<string, any>) => {
                            return cell.get('CommentId') === fromId;
                        })
                        .keys();
                }
            }

            for (const fromId in rewriteMap) {
                if (rewriteMap.hasOwnProperty(fromId)) {
                    const toId = rewriteMap[fromId].toId;
                    // Log.info (`Rewriting data, changing commentIds from ${fromId} to ${toId}`);

                    newData = newData.withMutations((mutable: Imm.Map<string, any>) => {
                        const iterator = rewriteMap[fromId].cellIds;

                        for (let it = iterator.next(); it && !it.done; it = iterator.next()) {
                            const cellId = it.value;
                            let cell = mutable.get(cellId);
                            cell = cell.set('CommentId', toId);
                            mutable.set(cellId, cell);
                        }
                    });
                }
            }

            // Process cell data

            newData = processCellData(newData, (res: string) => {
                const commentId = generateKey({
                    forMap: comments,
                    sourceText: res,
                    chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
                    allowCharsNotInSource: true,
                    allowNumerical: false,
                });
                comments = comments.set(
                    commentId,
                    Imm.fromJS({
                        text: res,
                        importance: estimateImportance(res),
                    })
                );
                return commentId;
            });

            // Reiterate over the affected rows to update minVal/maxVal

            rowDefs = state.getIn(['cumulReports', repId, 'rowDefs']);

            for (const rowId in affectedRows) {
                if (affectedRows.hasOwnProperty(rowId)) {
                    const cellId = `${colId}_${rowId}`;
                    const cell = newData.get(cellId);

                    if (cell && cell.has('val')) {
                        const val = cell.get('val');
                        let minVal = rowDefs.getIn([rowId, 'minVal']);
                        let maxVal = rowDefs.getIn([rowId, 'maxVal']);

                        if (typeof minVal === 'undefined') {
                            minVal = val;
                        } else {
                            minVal = Math.min(val, minVal);
                        }

                        if (typeof maxVal === 'undefined') {
                            maxVal = val;
                        } else {
                            maxVal = Math.max(val, maxVal);
                        }

                        rowDefs = rowDefs.setIn([rowId, 'minVal'], minVal);
                        rowDefs = rowDefs.setIn([rowId, 'maxVal'], maxVal);
                    }
                }
            }

            state = state
                .mergeIn(['cumulReports', repId, 'data'], newData)
                .setIn(['cumulReports', repId, 'rowDefs'], rowDefs)
                .setIn(['cumulReports', repId, 'comments'], comments);
        } catch (e) {
            Log.error('loadCumulDataSuccess: Exception: ' + e.message);
        }

        return decCounter(state, ['cumulReports', repId, 'loading']);
    },
    failure: (state, action) => {
        Log.warn('Failed to load the cumul data!', action);
        const repId = `${action.meta.patnr}_${action.meta.defsId}`;
        return decCounter(state, ['cumulReports', repId, 'loading']);
    },
};
